// Features Section Component
// ==========================
// Feature #287: Enhanced with design-line accents and refined styling

import React from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../ui/Card';
import DesignLine from '../ui/DesignLine';

interface Feature {
  icon: string;
  titleKey: string;
  descriptionKey: string;
}

const features: Feature[] = [
  {
    icon: '📄',
    titleKey: 'features.smartImport.title',
    descriptionKey: 'features.smartImport.description',
  },
  {
    icon: '💬',
    titleKey: 'features.learnThroughDialogue.title',
    descriptionKey: 'features.learnThroughDialogue.description',
  },
  {
    icon: '🎯',
    titleKey: 'features.adaptsToYou.title',
    descriptionKey: 'features.adaptsToYou.description',
  },
  {
    icon: '🔄',
    titleKey: 'features.fourModes.title',
    descriptionKey: 'features.fourModes.description',
  },
];

const FeaturesSection: React.FC = () => {
  const { t } = useTranslation('landing');

  return (
    <section id="features" className="py-20 md:py-28 bg-canvas dark:bg-background-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          {/* Feature #280: Serif display font for headings */}
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-neutral-dark dark:text-white mb-4">
            {t('features.title')}
          </h2>
          <p className="text-lg text-neutral-medium dark:text-gray-400 max-w-2xl mx-auto mb-6 font-sans">
            {t('features.subtitle')}
          </p>
          {/* Feature #287: Design-line accent */}
          <div className="flex justify-center">
            <DesignLine variant="horizontal" width="80px" height={2} />
          </div>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <Card
              key={index}
              className="text-center group hover:border-primary-200 dark:hover:border-primary-800 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-up"
              padding="lg"
              style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'both' }}
            >
              {/* Icon */}
              <div className="w-16 h-16 mx-auto mb-6 bg-primary-50 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-3xl">{feature.icon}</span>
              </div>

              {/* Title */}
              <h3 className="text-xl font-semibold text-neutral-dark dark:text-white mb-3">
                {t(feature.titleKey)}
              </h3>

              {/* Description */}
              <p className="text-neutral-medium dark:text-gray-400 leading-relaxed">
                {t(feature.descriptionKey)}
              </p>
            </Card>
          ))}
        </div>

        {/* Additional features list - Feature #151, #287: Refined badges with new frame styling */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { icon: '💬', labelKey: 'features.badges.voiceBased' },
            { icon: '🇩🇰', labelKey: 'features.badges.danishAI' },
            { icon: '📊', labelKey: 'features.badges.progress' },
            { icon: '🧠', labelKey: 'features.badges.adaptive' },
            { icon: '📱', labelKey: 'features.badges.responsive' },
            { icon: '🔒', labelKey: 'features.badges.secure' },
          ].map((item, index) => (
            <div
              key={index}
              className="group flex items-center justify-center space-x-2 py-3 px-4 bg-canvas dark:bg-canvas-dark border border-frame-border dark:border-frame-border-dark rounded-lg hover:shadow-md transition-shadow animate-fade-in-up"
              style={{ animationDelay: `${0.4 + index * 0.05}s`, animationFillMode: 'both' }}
            >
              <span className="text-lg group-hover:scale-110 transition-transform">{item.icon}</span>
              <span className="text-sm font-medium text-neutral-dark dark:text-gray-300 font-sans">
                {t(item.labelKey)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
